





























import { defineComponent, reactive, toRefs, computed, ref, onMounted, PropType } from '@vue/composition-api';
import * as Common from "../commons/common"
import http from "../plugins/http";
import { Sheet, SheetField, SheetActivity, FieldActivity, SaitenModeEnum, CorrectStateEnum, Point } from "../modules/teacher/SaitenInput";

export interface Data {
  markSize: number
  isDrawing: boolean
  isTouching: boolean
  isTouchScoringEnable: boolean
  startPos: Point
  endPos: Point
  CorrectStateEnum: Readonly<{Seitou: number; Gotou: number; Tokuten: number}>
  SaitenModeEnum: Readonly<{None: number; KeyInput: number; SeitouClick: number; GotouClick: number; TokutenClick: number; 
                            FreeLine: number; StraightLine: number; BracesStart: number; BracesEnd: number; Number: number; Eraser: number; TextStamp: number;}>
}

export default defineComponent({
  props: {
    sheetField: {
      type: SheetField as PropType<SheetField>,
      required: true
    },
    fieldActivity: {
      type: FieldActivity as PropType<FieldActivity>,
      required: true
    },
    sheet_id: {
      type: String as PropType<string>,
      required: true
    },
    idx: {
      type: Number as PropType<number>,
      required: true
    },
    selectedIdx: {
      type: Number as PropType<number>,
      required: true
    },
    scale: {
      type: Number as PropType<number>,
      required: true
    },
    thickness: {
      type: Number as PropType<number>,
      required: true
    },
    fieldPadding: {
      type: Number as PropType<number>,
      required: true
    },
    fieldMargin: {
      type: Number as PropType<number>,
      required: true
    },
    fieldWrapWidth: {
      type: Number as PropType<number>,
      required: true
    },
    fieldWrapHeight: {
      type: Number as PropType<number>,
      required: true
    },
    fieldImageWidth: {
      type: Number as PropType<number>,
      required: true
    },
    fieldImageHeight: {
      type: Number as PropType<number>,
      required: true
    },
    saitenMode: {
      type: Number as PropType<number>,
      required: true
    },
    tensakuText: {
      type: String as PropType<string>,
      required: true
    },
    tensakuFontSize: {
      type: Number as PropType<number>,
      required: true
    },
    stampText: {
      type: String as PropType<string>,
      required: true
    },
    stampFontSize: {
      type: Number as PropType<number>,
      required: true
    },
    isStampVertical: {
      type: Boolean as PropType<boolean>,
      required: true
    },
  },
  setup (props, context) {

    /************************************************************
     * Data
     ************************************************************/

    const state = reactive<Data>({
      markSize: 50,
      isDrawing: false,
      isTouching: false,
      isTouchScoringEnable: true,
      startPos: new Point(0, 0),
      endPos: new Point(0, 0),
      CorrectStateEnum: CorrectStateEnum,
      SaitenModeEnum: SaitenModeEnum,
    });
    
    /************************************************************
     * 切り出し画像の親DivのCSS
     ************************************************************/

    const fieldWrapStyle = computed((): any => {
      const style: any = {};
      style["width"] = `${props.fieldWrapWidth}px`;
      style["height"] = `${props.fieldWrapHeight}px`;
      style["padding"] = `${props.fieldPadding}px`;
      style["margin-left"] = `${props.fieldMargin}px`;
      style["margin-right"] = `${props.fieldMargin}px`;

      if(props.fieldActivity.correct_state == null){
        style["background-color"] = "#c0c0e2";
      }else if(props.fieldActivity.correct_state == 1){
        style["background-color"] = "#31b0e4";
      }else if(props.fieldActivity.correct_state == -1){
        style["background-color"] = "pink";
      }else if(props.fieldActivity.correct_state == 0){
        style["background-color"] = "#cfd609";
      }

      if(props.selectedIdx == props.idx){
        style["box-shadow"] = "0 0 4px 4px blue";
      }
      return style;
    });

    /************************************************************
     * 切り出し画像div・imgタグ・canvasタグのCSS
     ************************************************************/

    const fieldImageStyle = computed((): any => {
      const style: any = {};
      style["width"] = `${props.fieldImageWidth}px`;
      style["height"] = `${props.fieldImageHeight}px`;
      return style;
    });

    /************************************************************
     * ヘッダー出席番号のCSS
     ************************************************************/

    const headerStyle = computed((): any => {
      const style: any = {};
      style["margin-left"] = `${props.fieldMargin}px`;
      return style;
    });

    /************************************************************
     * 採点マークimgタグのCSS
     ************************************************************/

    const markImageStyle = computed((): any => {
      const style: any = {};
      style["width"] = `${state.markSize}px`;
      style["height"] = `${state.markSize}px`;
      return style;
    });

    /************************************************************
     * 採点マーク部分点spanタグのCSS
     ************************************************************/

    const markScoreStyle = computed((): any => {
      const style: any = {};
      style["font-size"] = `${state.markSize / 2}px !important`;
      style["top"] = `${state.markSize / 5}px`;
      style["left"] = `${props.fieldActivity.score}`.length == 1 ? `${state.markSize / 4}px` : `${state.markSize / 5}px`;
      return style;
    });

    /************************************************************
     * 現在のモードが添削系か判定
     ************************************************************/

    const isTensakuMode = computed(() => {
      return props.saitenMode != state.SaitenModeEnum.None && props.saitenMode != state.SaitenModeEnum.KeyInput && props.saitenMode != state.SaitenModeEnum.SeitouClick
           && props.saitenMode != state.SaitenModeEnum.GotouClick && props.saitenMode != state.SaitenModeEnum.TokutenClick;
    });

    /************************************************************
     * 設問の画像上をクリック
     ************************************************************/

    const fieldClicked = () => {
      if(!isTensakuMode.value){
        context.emit("field-clicked", props.idx);
      }
    }

    /************************************************************
     * canvasのBase64文字列をemit
     ************************************************************/

    const setBase64Canvas = () => {
      if(props.fieldActivity.isCanvasEdited){
        const canvas = document.getElementById(`canvas-${props.idx}`) as HTMLCanvasElement;
        context.emit("set-base64-canvas", props.idx, canvas.toDataURL("image/png").replace("data:image/png;base64,", ""));
      }
    };

    /************************************************************
     * マウス座標取得
     ************************************************************/

    const getMousePos = (e: MouseEvent) => {
      const x: number = e.offsetX / props.scale;
      const y: number = e.offsetY / props.scale;
      return new Point(x, y);
    };

    /************************************************************
     * canvas MouseDown
     ************************************************************/

    const canvasMouseDown = (e: MouseEvent) => {
      
      // 添削モード以外はリターン
      if(!isTensakuMode.value){
        return;
      }
      
      state.startPos = getMousePos(e);
      state.isDrawing = true;

      if(props.saitenMode == state.SaitenModeEnum.BracesStart || props.saitenMode == state.SaitenModeEnum.BracesEnd){
        const ctx = (e.target as HTMLCanvasElement).getContext("2d");
        drawBrace(ctx);
      }else if(props.saitenMode == state.SaitenModeEnum.Number){
        const ctx = (e.target as HTMLCanvasElement).getContext("2d");
        drawText(ctx);
      }else if(props.saitenMode == state.SaitenModeEnum.Eraser){
        const ctx = (e.target as HTMLCanvasElement).getContext("2d");
        ctx.globalCompositeOperation = "destination-out";
      }else if(props.saitenMode == state.SaitenModeEnum.TextStamp){
        const ctx = (e.target as HTMLCanvasElement).getContext("2d");
        drawTextStamp(ctx);
      }
    };

    /************************************************************
     * canvas MouseMove
     ************************************************************/

    const canvasMouseMove = (e: MouseEvent) => {
      
      if(!state.isDrawing || (props.saitenMode != state.SaitenModeEnum.FreeLine && props.saitenMode != state.SaitenModeEnum.Eraser)){
        return;
      }

      state.endPos = getMousePos(e);
      const ctx = (e.target as HTMLCanvasElement).getContext("2d");
      drawLine(ctx);
      state.startPos = state.endPos;
    };


    /************************************************************
     * canvas MouseUp
     ************************************************************/

    const canvasMouseUp = (e: MouseEvent) => {
      
      if(!state.isDrawing){
        return;
      }
      
      if(props.saitenMode == state.SaitenModeEnum.Eraser){
        const ctx = (e.target as HTMLCanvasElement).getContext("2d");
        ctx.globalCompositeOperation = "source-over";
      }else if(props.saitenMode == state.SaitenModeEnum.StraightLine){
        state.endPos = getMousePos(e);
        const ctx = (e.target as HTMLCanvasElement).getContext("2d");
        drawLine(ctx);
      }

      state.isDrawing = false;
    };

    /************************************************************
     * canvas MouseLeave
     ************************************************************/

    const canvasMouseLeave = (e: MouseEvent) => {
      state.isDrawing = false;
    };


    /************************************************************
     * canvas Touchイベントの座標取得
     ************************************************************/

    const getTouchPos = (t: Touch, canvas: HTMLCanvasElement) => {
      const x: number = (t.clientX - canvas.getBoundingClientRect().left) / props.scale;
      const y: number = (t.clientY - canvas.getBoundingClientRect().top) / props.scale;
      return new Point(x, y);
    }

    /************************************************************
     * canvas TouchStart
     ************************************************************/

    const touchStart = (e: TouchEvent) => {

      if(isTensakuMode.value){

        // 添削モードの場合
        e.preventDefault();

        state.startPos = getTouchPos(e.changedTouches[0], e.target as HTMLCanvasElement);
        state.isDrawing = true;
        
        if(props.saitenMode == state.SaitenModeEnum.BracesStart || props.saitenMode == state.SaitenModeEnum.BracesEnd){
          const ctx = (e.target as HTMLCanvasElement).getContext("2d");
          drawBrace(ctx);
        }else if(props.saitenMode == state.SaitenModeEnum.Number){
          const ctx = (e.target as HTMLCanvasElement).getContext("2d");
          drawText(ctx);
          console.log("touch start");
        }else if(props.saitenMode == state.SaitenModeEnum.Eraser){
          const ctx = (e.target as HTMLCanvasElement).getContext("2d");
          ctx.globalCompositeOperation = "destination-out";
        }else if(props.saitenMode == state.SaitenModeEnum.TextStamp){
          const ctx = (e.target as HTMLCanvasElement).getContext("2d");
          drawTextStamp(ctx);
        }
      }else if(state.isTouchScoringEnable && props.saitenMode == state.SaitenModeEnum.KeyInput){
        // キー入力の場合
        e.preventDefault();
        state.isTouching = true;
        state.startPos = getTouchPos(e.changedTouches[0], e.target as HTMLCanvasElement);
      }else{
        // 上記以外はリターン
        return;
      }
    };

    /************************************************************
     * canvas TouchMove
     ************************************************************/

    const touchMove = (e: TouchEvent) => {
      
      if(state.isDrawing && (props.saitenMode == state.SaitenModeEnum.FreeLine || props.saitenMode == state.SaitenModeEnum.Eraser)){
        e.preventDefault();
        const canvas = e.target as HTMLCanvasElement;
        state.endPos = getTouchPos(e.changedTouches[0], canvas);
        const ctx = canvas.getContext("2d");
        drawLine(ctx);
        state.startPos = state.endPos;
      }else if(state.isTouchScoringEnable && state.isTouching && props.saitenMode == state.SaitenModeEnum.KeyInput){
        e.preventDefault();
      }else{
        return;
      }
    };

    /************************************************************
     * canvas TouchEnd
     ************************************************************/

    const touchEnd = (e: TouchEvent) => {
      
      if(state.isDrawing){
        e.preventDefault();
      
        if(props.saitenMode == state.SaitenModeEnum.Eraser){
          const ctx = (e.target as HTMLCanvasElement).getContext("2d");
          ctx.globalCompositeOperation = "source-over";
        }else if(props.saitenMode == state.SaitenModeEnum.StraightLine){
          const canvas = e.target as HTMLCanvasElement;
          state.endPos = getTouchPos(e.changedTouches[0], canvas);
          const ctx = canvas.getContext("2d");
          drawLine(ctx);
        }

        state.isDrawing = false;
      }else if(state.isTouchScoringEnable && state.isTouching){
        e.preventDefault();
        state.isTouching = false;
        const canvas = e.target as HTMLCanvasElement;
        state.endPos = getTouchPos(e.changedTouches[0], canvas);

        // 始点・終点の距離でフリック判定
        const horizon = state.endPos.x - state.startPos.x;
        const vertical = state.endPos.y - state.startPos.y;
        const diffH = horizon >= 0 ? horizon : -horizon;
        const diffV = vertical >= 0 ? vertical : -vertical;

        if(diffH > diffV && diffH >= 50){
          if(horizon >= 0){
            // 右方向
            context.emit("set-correct-by-child", props.idx, state.CorrectStateEnum.Seitou);
          }else{
            // 左方向
            context.emit("set-correct-by-child", props.idx, state.CorrectStateEnum.Gotou);
          }
        }else if(diffV > diffH && diffV >= 50){
          if(vertical >= 0){
            // 下方向
            context.emit("set-correct-by-child", props.idx, state.CorrectStateEnum.Tokuten);
          }
        }
        
      }else{
        return;
      }
    };



    /************************************************************
     * canvas 自由線・消しゴム描画
     ************************************************************/

    const drawLine = (ctx: CanvasRenderingContext2D) => {
      if(props.saitenMode == SaitenModeEnum.Eraser){
        const thickness = props.thickness * 16;
        ctx.beginPath();
        ctx.fillRect(state.startPos.x - (2 * props.scale), state.startPos.y - (2 * props.scale), thickness, thickness);
      }else {
        ctx.lineWidth = props.thickness;
        ctx.beginPath();
        ctx.moveTo(state.startPos.x, state.startPos.y);
        ctx.lineTo(state.endPos.x, state.endPos.y);
        ctx.stroke();
      }

      context.emit("set-canvas-edited", props.idx);
    };

    /************************************************************
     * canvas カッコ描画
     ************************************************************/

    const drawBrace = (ctx: CanvasRenderingContext2D) => {
      const braceSize = 30;
      ctx.lineWidth = props.thickness;
      
      ctx.beginPath();

      if(props.saitenMode == state.SaitenModeEnum.BracesStart){
        ctx.moveTo(state.startPos.x + braceSize, state.startPos.y);
        ctx.lineTo(state.startPos.x, state.startPos.y);
        ctx.stroke();
        ctx.moveTo(state.startPos.x, state.startPos.y);
        ctx.lineTo(state.startPos.x, state.startPos.y + braceSize);
        ctx.stroke();
      }else{
        ctx.moveTo(state.startPos.x, state.startPos.y - braceSize);
        ctx.lineTo(state.startPos.x, state.startPos.y);
        ctx.stroke();
        ctx.moveTo(state.startPos.x, state.startPos.y);
        ctx.lineTo(state.startPos.x - braceSize, state.startPos.y);
        ctx.stroke();
      }

      context.emit("set-canvas-edited", props.idx);
    }

    /************************************************************
     * canvas 文字列描画
     ************************************************************/

    const drawText = (ctx: CanvasRenderingContext2D) => {
      if(props.tensakuText.length == 0){
        return;
      }
      ctx.font = `${props.tensakuFontSize}px 'Glyphicons kacho-hugetsu'`;
      context.root.$nextTick(() => {
        ctx.fillText(props.tensakuText, state.startPos.x, state.startPos.y);
      });

      context.emit("set-canvas-edited", props.idx);
    };

    /************************************************************
     * canvas 文字列スタンプ描画
     ************************************************************/

    const drawTextStamp = (ctx: CanvasRenderingContext2D) => {
      if(props.stampText.length == 0){
        return;
      }
      ctx.font = `${props.stampFontSize}px 'Glyphicons kacho-hugetsu'`;
      context.root.$nextTick(() => {
        if(!props.isStampVertical){
          ctx.fillText(props.stampText, state.startPos.x, state.startPos.y);
        }else{
          // 縦書き
          tategaki(ctx, props.stampText, state.startPos.x, state.startPos.y);
        }
      });

      context.emit("set-canvas-edited", props.idx);
    };

    /************************************************************
     * キャンバスのテキストを縦書きにする
     ************************************************************/

    const tategaki = (ctx: CanvasRenderingContext2D, text: string, x: number, y: number) => {
      var textList = text.split('\n');
      var lineHeight = ctx.measureText("あ").width;
      textList.forEach(function(elm, i) {
        Array.prototype.forEach.call(elm, function(ch, j) {
          ctx.fillText(ch, x-lineHeight*i, y+lineHeight*j);
        });
      });
    };

    /************************************************************
     * 番号押下で全体画像表示
     ************************************************************/

    const showPreviewImage = () => {
      context.emit("show-preview-image", props.idx);
    };

    /************************************************************
     * mounted
     ************************************************************/

    onMounted(async () => {
      // 切り出し画像キャッシュがない場合はAPIでダウンロード
      if(props.fieldActivity.base64Image == null){
        await http.get(`/api/saiten_input/download_cut_image/${props.sheet_id}/${props.fieldActivity.id}`).then(response => {
          context.emit("set-base64-image", props.idx, response.data.base64_image);
        });
      }

      const canvas = document.getElementById(`canvas-${props.idx}`) as HTMLCanvasElement;
      const ctx = canvas.getContext("2d");
      ctx.strokeStyle = "red";
      ctx.fillStyle = "red";
      ctx.lineJoin = "round";
      ctx.lineCap = "round";
      
      // フォントファイルをロードするために範囲外にダミーで書き込み処理を行う
      // これをやらないと1回目のテキストのフォントに花鳥風月が反映されない
      ctx.font = `${props.tensakuFontSize}px 'Glyphicons kacho-hugetsu'`;
      ctx.fillText(props.tensakuText, -100, -100);

      // canavs画像が存在する場合はロード
      if(props.fieldActivity.base64Canvas != null){
        const canvasImage = new Image();
        canvasImage.src = `data:image/png;base64,${props.fieldActivity.base64Canvas}`;
        canvasImage.onload = () => {
          ctx.drawImage(canvasImage, 0, 0);
        };
      }
    });

    return {
      ...toRefs(state),
      fieldWrapStyle,
      fieldImageStyle,
      markImageStyle,
      markScoreStyle,
      headerStyle,
      fieldClicked,
      setBase64Canvas,
      canvasMouseDown,
      canvasMouseMove,
      canvasMouseUp,
      canvasMouseLeave,
      touchStart,
      touchMove,
      touchEnd,
      isTensakuMode,
      showPreviewImage,
    }
  }
});
